/**
 * SvgBuilder - Orchestrates SVG generation for animation sequences
 */

import type { AnimationSequence } from '../../../parser/types';
import { AnimationStateManager } from '../../state/AnimationStateManager';
import type { AnimationState } from '../../types';
import { ParticipantLayoutCalculator } from '../layout/ParticipantLayout';
import { InteractionLayoutCalculator } from '../layout/InteractionLayout';
import type { LayoutConfig, ParticipantLayout } from '../layout/types';
import {
  renderParticipants,
  DEFAULT_PARTICIPANT_OPTIONS,
  type ParticipantRenderOptions,
} from './ParticipantRenderer';
import {
  renderInteractions,
  renderArrowheadMarkers,
  DEFAULT_INTERACTION_OPTIONS,
  type InteractionRenderOptions,
  type ArrowPath,
} from './InteractionRenderer';
import {
  renderNotes,
  DEFAULT_NOTE_OPTIONS,
  type NoteRenderOptions,
} from './NoteRenderer';
import {
  DEFAULT_FRAGMENT_OPTIONS,
  type FragmentRenderOptions,
} from './FragmentRenderer';

export interface SvgBuilderOptions {
  /**
   * Width of the SVG canvas
   */
  width: number;
  /**
   * Height of the SVG canvas
   */
  height: number;
  /**
   * Padding around the content
   */
  padding: number;
  /**
   * Options for rendering participants
   */
  participantOptions: ParticipantRenderOptions;
  /**
   * Options for rendering interactions
   */
  interactionOptions: InteractionRenderOptions;
  /**
   * Options for rendering notes
   */
  noteOptions: NoteRenderOptions;
  /**
   * Options for rendering fragments
   */
  fragmentOptions: FragmentRenderOptions;
}

/**
 * Default options for SVG builder
 */
export const DEFAULT_SVG_OPTIONS: SvgBuilderOptions = {
  width: 800,
  height: 600,
  padding: 150, // Adds visual padding to prevent cutoff at edges
  participantOptions: DEFAULT_PARTICIPANT_OPTIONS,
  interactionOptions: DEFAULT_INTERACTION_OPTIONS,
  noteOptions: DEFAULT_NOTE_OPTIONS,
  fragmentOptions: DEFAULT_FRAGMENT_OPTIONS,
};

/**
 * SvgBuilder - Main class for generating SVG from AnimationSequence
 */
export class SvgBuilder {
  private readonly ast: AnimationSequence;
  private readonly options: SvgBuilderOptions;
  private readonly stateManager: AnimationStateManager;
  private readonly layoutConfig: LayoutConfig;

  constructor(
    ast: AnimationSequence,
    options: Partial<SvgBuilderOptions> = {}
  ) {
    this.ast = ast;
    this.options = { ...DEFAULT_SVG_OPTIONS, ...options };

    // Initialize state manager
    this.stateManager = new AnimationStateManager(ast);

    // Create layout config from options
    // Note: Use small margins for layout positioning; padding is applied separately
    // to the SVG dimensions to create visual breathing room
    this.layoutConfig = {
      width: this.options.width,
      height: this.options.height,
      participantWidth: this.options.participantOptions.boxWidth,
      participantHeight: this.options.participantOptions.boxHeight,
      participantSpacing: 240, // Horizontal spacing between participants
      marginTop: this.options.participantOptions.startY,
      marginBottom: 20,
      marginLeft: 20, // Small margin for layout positioning
      marginRight: 20, // Small margin for layout positioning
      interactionSpacing: 80, // Vertical spacing between interactions
    };
  }

  /**
   * Generates the complete SVG document
   */
  public build(): string {
    const parts: string[] = [];

    // First, calculate the actual height needed for all interactions
    const states = this.stateManager.computeStates();
    let currentY =
      this.layoutConfig.marginTop +
      this.layoutConfig.participantHeight +
      this.layoutConfig.interactionSpacing;

    // Calculate final Y position
    for (let i = 0; i < states.length; i++) {
      const state = states[i];
      if (!state) continue;

      if (state.interactions.length > 0) {
        currentY +=
          state.interactions.length * this.layoutConfig.interactionSpacing;
      }
    }

    // Add space for bottom participant boxes and margin
    const actualHeight =
      currentY +
      this.layoutConfig.interactionSpacing +
      this.layoutConfig.participantHeight +
      this.layoutConfig.marginBottom;

    // Update layout config with actual height
    const actualLayoutConfig = {
      ...this.layoutConfig,
      height: actualHeight,
    };

    // Calculate participant positions with actual height
    const participantCalculator = new ParticipantLayoutCalculator(
      this.ast.participants,
      actualLayoutConfig
    );
    const participantLayouts = participantCalculator.calculateLayouts();

    // Calculate content bounding box
    const bounds = this.calculateContentBounds(participantLayouts);

    // SVG opening tag with viewBox based on actual content bounds
    parts.push(this.renderSvgOpen(bounds));

    // CSS styles
    parts.push(this.renderStyles());

    // Arrowhead markers
    parts.push(
      renderArrowheadMarkers(this.options.interactionOptions.arrowheadSize)
    );

    // Render participants and lifelines with actual height
    const participantArray = Array.from(participantLayouts.values());
    parts.push(
      renderParticipants(
        participantArray,
        actualHeight,
        this.options.participantOptions
      )
    );

    // Reset currentY for rendering interactions
    currentY =
      this.layoutConfig.marginTop +
      this.layoutConfig.participantHeight +
      this.layoutConfig.interactionSpacing;

    for (let i = 0; i < states.length; i++) {
      const state = states[i];

      if (!state) continue;

      // Render participant visibility state for this step
      parts.push(
        this.renderParticipantVisibility(
          state,
          participantLayouts,
          i,
          actualHeight
        )
      );

      if (state.interactions.length > 0) {
        // Calculate interaction layouts starting from currentY
        const interactionCalculator = new InteractionLayoutCalculator(
          state.interactions,
          participantLayouts,
          this.layoutConfig,
          currentY
        );
        const interactionLayouts = interactionCalculator.calculateLayouts();

        // Calculate vertical span for activation boxes
        const activationStartY =
          currentY - this.layoutConfig.interactionSpacing / 2;
        const activationEndY =
          currentY +
          state.interactions.length * this.layoutConfig.interactionSpacing -
          this.layoutConfig.interactionSpacing / 2;

        // Render step background color if specified (behind all content)
        const step = this.ast.steps[i];
        if (step?.color) {
          parts.push(
            this.renderStepBackground(
              step.color,
              i,
              activationStartY,
              activationEndY,
              this.layoutConfig.width
            )
          );
        }

        // Render activation boxes (behind interactions)
        parts.push(
          this.renderActivationBoxes(
            state,
            participantLayouts,
            i,
            activationStartY,
            activationEndY
          )
        );

        // Update currentY for next step's interactions
        currentY +=
          state.interactions.length * this.layoutConfig.interactionSpacing;

        // Convert to ArrowPath with labels and dimming info
        const arrows: ArrowPath[] = interactionLayouts.map((layout, idx) => {
          const interaction = state.interactions[idx];
          const fromParticipant = state.participants.get(layout.from);
          const toParticipant = state.participants.get(layout.to);

          // Arrow is dimmed if either participant is dimmed
          const isDimmed =
            fromParticipant?.visibility === 'dimmed' ||
            toParticipant?.visibility === 'dimmed';

          return {
            ...layout,
            label: interaction?.message ?? '',
            isDimmed,
          };
        });

        if (arrows.length > 0) {
          parts.push(
            renderInteractions(arrows, i, this.options.interactionOptions)
          );
        }

        // Render notes if any exist in this step
        if (step?.notes && step.notes.length > 0) {
          // Position notes below the last interaction
          const noteY = currentY - this.layoutConfig.interactionSpacing / 2;
          parts.push(
            renderNotes(
              step.notes,
              participantLayouts,
              noteY,
              i,
              this.options.noteOptions
            )
          );
        }
      }

      // Render fragments if any exist in this step
      if (state.fragments && state.fragments.length > 0) {
        // Fragments will be rendered in a future update
        // For now, we acknowledge their presence in the step
        // TODO: Implement fragment interaction layout and rendering
      }
    }

    // SVG closing tag
    parts.push('</svg>');

    return parts.join('\n');
  }

  /**
   * Renders participant visibility overlays for a specific step
   * Applies dimming effects based on participant visibility state
   */
  private renderParticipantVisibility(
    state: AnimationState,
    participantLayouts: Map<string, ParticipantLayout>,
    stepIndex: number,
    totalHeight: number
  ): string {
    const parts: string[] = [];
    parts.push(`<g class="step-visibility" data-step="${stepIndex}">`);

    // Render dimming overlays for participants
    state.participants.forEach((participant, name) => {
      const layout = participantLayouts.get(name);
      if (!layout) return;

      if (participant.visibility === 'dimmed') {
        // Render a semi-transparent overlay over the participant area
        const overlayY = this.options.participantOptions.startY;
        const overlayHeight = totalHeight - overlayY;

        parts.push(
          `  <rect x="${layout.x - 10}" y="${overlayY}" ` +
            `width="${layout.width + 20}" height="${overlayHeight}" ` +
            `class="participant-dim-overlay" fill="var(--svg-bg)" opacity="0.7" />`
        );
      }
    });

    parts.push('</g>');
    return parts.join('\n');
  }

  /**
   * Renders activation boxes for activated participants
   * Activation boxes appear as rectangles on the lifeline showing when a participant is processing
   */
  private renderActivationBoxes(
    state: AnimationState,
    participantLayouts: Map<string, ParticipantLayout>,
    stepIndex: number,
    startY: number,
    endY: number
  ): string {
    const parts: string[] = [];
    parts.push(`<g class="step-activations" data-step="${stepIndex}">`);

    state.participants.forEach((participant, name) => {
      if (!participant.activated) return;

      const layout = participantLayouts.get(name);
      if (!layout) return;

      // Activation box is a narrow rectangle on the lifeline
      const boxWidth = 12;
      const boxX = layout.centerX - boxWidth / 2;

      parts.push(
        `  <rect x="${boxX}" y="${startY}" ` +
          `width="${boxWidth}" height="${endY - startY}" ` +
          `class="activation-box" fill="#e8f4f8" stroke="#4a90e2" stroke-width="1.5" />`
      );
    });

    parts.push('</g>');
    return parts.join('\n');
  }

  /**
   * Renders a colored background for a step
   * Background spans the full width and the vertical height of step interactions
   */
  private renderStepBackground(
    color: string,
    stepIndex: number,
    startY: number,
    endY: number,
    width: number
  ): string {
    const parts: string[] = [];
    parts.push(`<g class="step-background" data-step="${stepIndex}">`);

    parts.push(
      `  <rect x="0" y="${startY}" ` +
        `width="${width}" height="${endY - startY}" ` +
        `fill="${color}" opacity="0.15" />`
    );

    parts.push('</g>');
    return parts.join('\n');
  }

  /**
   * Calculates the bounding box of all content
   */
  private calculateContentBounds(
    participantLayouts: Map<string, ParticipantLayout>
  ): { minX: number; minY: number; maxX: number; maxY: number } {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    // Get bounds from participant layouts
    participantLayouts.forEach((layout) => {
      minX = Math.min(minX, layout.x);
      minY = Math.min(minY, layout.y);
      maxX = Math.max(maxX, layout.x + layout.width);
      maxY = Math.max(maxY, layout.lifelineEnd);
    });

    // If no participants, use default bounds
    if (!isFinite(minX)) {
      minX = 0;
      minY = 0;
      maxX = this.options.width;
      maxY = this.options.height;
    }

    return { minX, minY, maxX, maxY };
  }

  /**
   * Renders the opening SVG tag with viewBox
   *
   * Uses padding around the content bounds to create visual breathing room.
   */
  private renderSvgOpen(bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  }): string {
    const { padding } = this.options;

    // ViewBox starts at content minX/minY minus padding
    const viewBoxX = bounds.minX - padding;
    const viewBoxY = bounds.minY - padding;
    const viewBoxWidth = bounds.maxX - bounds.minX + padding * 2;
    const viewBoxHeight = bounds.maxY - bounds.minY + padding * 2;

    // SVG dimensions match viewBox for 1:1 pixel mapping
    const svgWidth = viewBoxWidth;
    const svgHeight = viewBoxHeight;

    return (
      `<svg xmlns="http://www.w3.org/2000/svg" ` +
      `width="${svgWidth}" height="${svgHeight}" ` +
      `viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}">`
    );
  }

  /**
   * Renders CSS styles for the SVG
   */
  private renderStyles(): string {
    return `<style>
  .participant-box {
    fill: #f0f0f0;
    stroke: #333;
    stroke-width: 2;
  }
  .participant-subsystem {
    fill: #e3f2fd;
    stroke: #1976d2;
    stroke-width: 2.5;
    cursor: pointer;
  }
  .participant-subsystem:hover {
    fill: #bbdefb;
    stroke: #1565c0;
  }
  .subsystem-icon {
    color: #1976d2;
    pointer-events: none;
  }
  .participant-label {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 15px;
    font-weight: 600;
    fill: #333;
  }
  .lifeline {
    stroke: #999;
    stroke-width: 1;
  }
  .arrow-solid {
    stroke: var(--svg-arrow, #333);
    stroke-width: 2;
    fill: none;
  }
  .arrow-dashed {
    stroke: #666;
    stroke-width: 2;
    fill: none;
  }
  .arrow-async {
    stroke: #0066cc;
    stroke-width: 2;
    fill: none;
  }
  .arrowhead {
    fill: var(--svg-arrowhead, #333);
  }
  .arrow-label {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 12px;
    fill: var(--svg-text, #333);
  }
  .step-interactions {
    opacity: 1;
    transition: opacity 0.3s ease;
  }
  .step-interactions.hidden {
    opacity: 0;
  }
  .activation-box {
    transition: opacity 0.3s ease;
  }
  .step-activations {
    opacity: 1;
    transition: opacity 0.3s ease;
  }
  .step-activations.hidden {
    opacity: 0;
  }
  .note-box {
    fill: #fffbea;
    stroke: #f59e0b;
    stroke-width: 1.5;
  }
  .note-text {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 12px;
    fill: #92400e;
    font-style: italic;
  }
  .step-notes {
    opacity: 1;
    transition: opacity 0.3s ease;
  }
  .step-notes.hidden {
    opacity: 0;
  }
  .fragment-box {
    fill: none;
    stroke: #666;
    stroke-width: 1.5;
    stroke-dasharray: 5,3;
  }
  .fragment-label-box {
    fill: #f5f5f5;
    stroke: #666;
    stroke-width: 1.5;
  }
  .fragment-type {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: bold;
    fill: #333;
  }
  .fragment-condition {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    fill: #555;
    font-style: italic;
  }
  .step-fragments {
    opacity: 1;
    transition: opacity 0.3s ease;
  }
  .step-fragments.hidden {
    opacity: 0;
  }
  .fragment-alt .fragment-label-box {
    fill: #fef3c7;
  }
  .fragment-opt .fragment-label-box {
    fill: #dbeafe;
  }
  .fragment-loop .fragment-label-box {
    fill: #dcfce7;
  }
  .fragment-par .fragment-label-box {
    fill: #fce7f3;
  }
</style>`;
  }

  /**
   * Gets the title of the animation
   */
  public getTitle(): string {
    return this.ast.title;
  }

  /**
   * Gets the number of steps in the animation
   */
  public getStepCount(): number {
    return this.ast.steps.length;
  }

  /**
   * Gets the number of participants
   */
  public getParticipantCount(): number {
    return this.ast.participants.length;
  }
}
